import { Injectable, inject, ApplicationRef, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, interval, fromEvent } from 'rxjs';
import { first, filter } from 'rxjs/operators';

/**
 * PwaUpdateService gives the Resource Access PWA a deterministic update path.
 *
 * Angular's service worker downloads a new version in the background but, by
 * default, only activates it once every tab has closed. On an installed mobile
 * PWA that rarely happens, so workers keep serving a stale build indefinitely
 * even after a fresh deploy + CloudFront invalidation.
 *
 * This service closes that gap:
 *  - Checks for a new version on startup (once the app stabilizes).
 *  - Polls for updates on a fixed interval and whenever the app regains focus
 *    or becomes visible (e.g. the worker reopens the PWA).
 *  - When a new version is ready, activates it and hard-reloads the page so the
 *    client always ends up on the latest deployed build.
 *
 * Registered via APP_INITIALIZER so it starts as soon as the app boots.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);

  /** How often to poll the server for a new deployed version. */
  private readonly CHECK_INTERVAL_MS = 60 * 1000; // 60 seconds

  /** True while a version activation + reload is in progress (prevents loops). */
  private readonly updating = signal(false);

  /**
   * Wire up update detection. Safe to call even when the service worker is
   * disabled (dev mode) — it simply no-ops.
   */
  initialize(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    // React to a new version becoming ready to activate.
    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.activateAndReload());

    // Recover from an unrecoverable SW state by doing a clean reload.
    this.swUpdate.unrecoverable.subscribe(() => {
      // eslint-disable-next-line no-console
      console.warn('[PWA] Unrecoverable service worker state — reloading.');
      this.hardReload();
    });

    // Check once the app is stable, then poll on an interval.
    const appStable$ = this.appRef.isStable.pipe(first(stable => stable === true));
    const pollInterval$ = interval(this.CHECK_INTERVAL_MS);
    concat(appStable$, pollInterval$).subscribe(() => this.checkForUpdate());

    // Also check when the PWA regains focus / becomes visible again.
    fromEvent(window, 'focus').subscribe(() => this.checkForUpdate());
    fromEvent(document, 'visibilitychange').subscribe(() => {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    });
  }

  /** Ask the service worker to check the server for a newer version. */
  async checkForUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled || this.updating()) {
      return;
    }
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // Network hiccup — will retry on next poll.
    }
  }

  /** Activate the pending version and reload onto the latest build. */
  private async activateAndReload(): Promise<void> {
    if (this.updating()) {
      return;
    }
    this.updating.set(true);
    try {
      await this.swUpdate.activateUpdate();
    } catch {
      // Even if activation reports an error, a reload picks up the new SW.
    }
    this.hardReload();
  }

  /** Reload the page, bypassing the in-memory app state. */
  private hardReload(): void {
    document.location.reload();
  }
}
